import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Shield, Home, Loader2, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const hwToken = () => new URLSearchParams(window.location.search).get("t") || "";
const withToken = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(hwToken())}`;

export default function ConfirmArrival() {
  const { requestId } = useParams();
  const [jobInfo, setJobInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    if (!requestId) return;
    fetchJobInfo();
  }, [requestId]);

  const fetchJobInfo = async () => {
    try {
      const res = await fetch(withToken(`${API_BASE}/api/requests/${requestId}/public-arrival-info`));
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setError(data?.detail || "Unable to load job information");
        return;
      }
      if (data.homeowner_already_confirmed) {
        setConfirmed(true);
      }
      setJobInfo(data);
    } catch (err) {
      setError("Unable to connect. Please check your internet connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!code.trim() || code.length < 6) {
      setConfirmError("Please enter the 6-digit confirmation code");
      return;
    }
    setConfirmError("");
    setConfirming(true);
    try {
      const res = await fetch(withToken(`${API_BASE}/api/requests/${requestId}/homeowner-confirm`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setConfirmError(data?.detail || "Invalid code. Please try again.");
        return;
      }
      setConfirmed(true);
    } catch (err) {
      setConfirmError("Connection error. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
          <p className="text-sm text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Unable to Load</h2>
            <p className="text-sm text-zinc-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm border-emerald-800 bg-zinc-900">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Arrival Confirmed</h2>
            <p className="text-sm text-zinc-400 mb-4">
              You have confirmed that {jobInfo?.technician_name || "the technician"} has arrived at your property.
            </p>
            <div className="p-3 bg-zinc-800 rounded-lg">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Service</p>
              <p className="text-sm text-white">{jobInfo?.service_type || "Field Service"}</p>
            </div>
            <p className="text-xs text-zinc-500 mt-4">
              You may close this page. The technician will begin work shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="confirm-arrival-page">
      <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900">
        <CardContent className="p-6 space-y-5">
          {/* Header */}
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
              <Shield className="w-7 h-7 text-red-400" />
            </div>
            <h1 className="text-lg font-bold text-white">Confirm Technician Arrival</h1>
            <p className="text-xs text-zinc-400 mt-1">
              Apex FieldProIQ
            </p>
          </div>

          {/* Job Info */}
          {jobInfo && (
            <div className="p-4 bg-zinc-800 rounded-xl space-y-2">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-zinc-500" />
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Technician</p>
                  <p className="text-sm text-white font-medium">{jobInfo.technician_name}</p>
                </div>
              </div>
              <div className="border-t border-zinc-700 pt-2">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Service</p>
                <p className="text-sm text-white">{jobInfo.service_type}</p>
              </div>
              {jobInfo.arrived_at && (
                <div className="border-t border-zinc-700 pt-2">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Arrived</p>
                  <p className="text-sm text-white">{new Date(jobInfo.arrived_at).toLocaleTimeString()}</p>
                </div>
              )}
            </div>
          )}

          {/* Code Input */}
          <div className="space-y-2">
            <label className="text-xs text-zinc-400 font-medium block">
              Enter the 6-digit code provided by the technician:
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setConfirmError("");
              }}
              placeholder="000000"
              maxLength={6}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-center text-2xl font-mono text-white tracking-[0.3em] placeholder:text-zinc-600 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              data-testid="homeowner-confirmation-code-input"
            />
            {confirmError && (
              <p className="text-xs text-red-400 text-center" data-testid="confirmation-error">
                {confirmError}
              </p>
            )}
          </div>

          {/* Confirm Button */}
          <Button
            onClick={handleConfirm}
            disabled={confirming || code.length < 6}
            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium"
            data-testid="confirm-arrival-submit"
          >
            {confirming ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <><CheckCircle2 className="w-5 h-5 mr-2" /> Confirm Arrival</>
            )}
          </Button>

          <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
            By confirming, you acknowledge that the Apex FieldProIQ technician 
            has arrived at your property to perform the requested service.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
